import { useState, useEffect, useCallback, useRef } from 'react';

interface UseTimerReturn {
  timeLeft: number;
  isRunning: boolean;
  isExpired: boolean;
  start: (seconds: number) => void;
  pause: () => void;
  resume: () => void;
  reset: () => void;
}

/**
 * Hook reutilizável para countdown timer
 * @param initialSeconds - Tempo inicial em segundos (opcional)
 * @returns Objeto com estado e controles do timer
 */
export function useTimer(initialSeconds?: number): UseTimerReturn {
  const [timeLeft, setTimeLeft] = useState<number>(initialSeconds || 0);
  const [isRunning, setIsRunning] = useState<boolean>(false);
  const [hasStarted, setHasStarted] = useState<boolean>(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const isExpired = hasStarted && timeLeft === 0 && !isRunning;

  const clearTimer = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const start = useCallback(
    (seconds: number) => {
      clearTimer();
      setTimeLeft(seconds);
      setIsRunning(true);
      setHasStarted(true);
    },
    [clearTimer]
  );

  const pause = useCallback(() => {
    clearTimer();
    setIsRunning(false);
  }, [clearTimer]);

  const resume = useCallback(() => {
    if (timeLeft > 0) {
      setIsRunning(true);
    }
  }, [timeLeft]);

  const reset = useCallback(() => {
    clearTimer();
    setTimeLeft(0);
    setIsRunning(false);
    setHasStarted(false);
  }, [clearTimer]);

  useEffect(() => {
    if (isRunning && timeLeft > 0) {
      intervalRef.current = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            setIsRunning(false);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } else {
      clearTimer();
    }

    return () => clearTimer();
  }, [isRunning, clearTimer]);

  return {
    timeLeft,
    isRunning,
    isExpired,
    start,
    pause,
    resume,
    reset,
  };
}
